/**
 * Service de l'espace « Mes factures » (régime invoice-only).
 *
 * Lecture transverse via api-metier `GET /v5/energyinvoices` — MÊME façade que
 * `/v5/energycontracts` (dérive `_id_company` de l'auth, délègue à courtage).
 * Aucune route `/api/courtage/...` ici : la lecture client passe par api-metier,
 * qui ne connaît pas la notion de `Finding` (frontière R1, SPEC §11.1).
 */

import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type {
    FactureItem,
    FactureItemApiResponse,
    FacturesListApiResponse,
    FacturesListResponse,
    FacturesQuery,
    FactureUploadAck,
    FactureUploadJob,
} from "@modules/courtage/types/facture-espace"

export class FacturesEspaceService {
    /**
     * Liste les factures de la company (ou d'un seul compteur via `query.pdl`).
     * `_id_company` est dérivé de l'auth côté serveur — jamais envoyé ici.
     */
    async list(
        query: FacturesQuery = {},
        controller?: AbortController
    ): Promise<FacturesListResponse> {
        return handleServiceCall<FacturesListResponse>(async () => {
            const params: Record<string, string> = {}
            if (query.pdl) params.pdl = query.pdl
            if (query.fournisseur) params.fournisseur = query.fournisseur
            if (query.q) params.q = query.q
            if (query.statut) params.statut = query.statut
            if (query.depuis) params.depuis = query.depuis
            if (query.jusqua) params.jusqua = query.jusqua
            if (query.sort) params.sort = query.sort
            if (query.order) params.order = query.order
            if (query.cursor) params.cursor = query.cursor
            if (query.limit !== undefined) params.limit = String(query.limit)
            const { data } = await axios.get<FacturesListApiResponse>(
                "/api/metier/v5/energyinvoices",
                { params, signal: controller?.signal }
            )
            return data.data
        })
    }

    /** Récupère une facture par son `_id` (`EnergyInvoice`). */
    async get(id: string, controller?: AbortController): Promise<FactureItem> {
        return handleServiceCall<FactureItem>(async () => {
            const { data } = await axios.get<FactureItemApiResponse>(
                `/api/metier/v5/energyinvoices/${id}`,
                { signal: controller?.signal }
            )
            return data.data
        })
    }

    /**
     * URL présignée (courte) du scan ORIGINAL d'une facture DÉTENUE.
     * api-metier vérifie la détention (`owns`) + délègue la presign à courtage ;
     * réservé aux factures `visible` avec `pdf_disponible` (404/403 sinon).
     */
    async getPdfUrl(
        id: string,
        controller?: AbortController
    ): Promise<{ url: string; expires_in: number }> {
        return handleServiceCall<{ url: string; expires_in: number }>(
            async () => {
                const { data } = await axios.get<{
                    data: { url: string; expires_in: number }
                }>(`/api/metier/v5/energyinvoices/${id}/pdf`, {
                    signal: controller?.signal,
                })
                return data.data
            }
        )
    }

    /**
     * Dépose des factures PDF (1..10) — `POST /v5/energyinvoices/upload`.
     * api-metier relaie à courtage (S3 + job + parse asynchrone). Retourne
     * l'accusé `{job_id, status, n_fichiers}` ; le parse est suivi via `getJob`.
     */
    async upload(
        files: File[],
        controller?: AbortController
    ): Promise<FactureUploadAck> {
        return handleServiceCall<FactureUploadAck>(async () => {
            const form = new FormData()
            for (const f of files) form.append("files", f, f.name)
            const { data } = await axios.post<{ data: FactureUploadAck }>(
                "/api/metier/v5/energyinvoices/upload",
                form,
                {
                    signal: controller?.signal,
                    headers: { "Content-Type": "multipart/form-data" },
                }
            )
            return data.data
        })
    }

    /** Suivi d'un job de dépôt — `GET /v5/energyinvoices/jobs/{id}`. */
    async getJob(
        jobId: string,
        controller?: AbortController
    ): Promise<FactureUploadJob> {
        return handleServiceCall<FactureUploadJob>(async () => {
            const { data } = await axios.get<{ data: FactureUploadJob }>(
                `/api/metier/v5/energyinvoices/jobs/${jobId}`,
                { signal: controller?.signal }
            )
            return data.data
        })
    }
}
